
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, X, Image, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ImageUploadProps {
  onImageUploaded: (url: string) => void;
  currentImage?: string;
  folder?: string;
  accept?: string;
  maxSize?: number;
}

const ImageUpload = ({ 
  onImageUploaded, 
  currentImage, 
  folder = "products", 
  accept = "image/*",
  maxSize = 5 * 1024 * 1024 // 5MB
}: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      onImageUploaded(data.publicUrl);
      
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.size > maxSize) {
        toast({
          title: "Error",
          description: `File size must be less than ${maxSize / (1024 * 1024)}MB`,
          variant: "destructive",
        });
        return;
      }
      uploadFile(file);
    }
  }, [maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { [accept]: [] },
    multiple: false,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
  });

  const removeImage = () => {
    onImageUploaded("");
  };

  return (
    <div className="space-y-4">
      {currentImage ? (
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <img
                src={currentImage}
                alt="Uploaded"
                className="w-full h-48 object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={removeImage}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className={`border-2 border-dashed transition-colors ${
          dragActive || isDragActive 
            ? "border-admin-accent-blue bg-admin-accent-blue/10" 
            : "border-admin-border"
        }`}>
          <CardContent className="p-6">
            <div
              {...getRootProps()}
              className="text-center cursor-pointer"
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="flex flex-col items-center space-y-2">
                  <Loader2 className="h-8 w-8 animate-spin text-admin-accent-blue" />
                  <p className="text-sm text-admin-muted-foreground">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-4">
                  <div className="p-4 bg-admin-accent-blue/10 rounded-full">
                    <Upload className="h-8 w-8 text-admin-accent-blue" />
                  </div>
                  <div>
                    <p className="text-admin-foreground font-medium">
                      Drop your image here, or click to browse
                    </p>
                    <p className="text-sm text-admin-muted-foreground mt-1">
                      Supports: JPG, PNG, GIF (max {maxSize / (1024 * 1024)}MB)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImageUpload;
